import { useEffect, useState, useCallback } from 'react'
import { FileText, Plus, Download, ChevronDown, Users, Clock, CheckCircle2, X, Pencil } from 'lucide-react'
import api from '../api/client'
import Loader from '../components/Loader'
import { successModal, errorModal } from '../components/ui'

// Admin — Termos e Documentos (versionados). Lista termos, estatísticas de aceite, histórico
// de versões e criação/edição. Leitura: GET /termos. Escrita: POST /termos (novo) · PUT /termos/{id}.
const fmt = (v) => (v ? new Date(v).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-')
const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent-400'
// PDFs ficam no host da API (/media); o build de produção não tem VITE_API_URL, então deriva do client.
const API = (import.meta.env.VITE_API_URL || api.defaults.baseURL || '').replace(/\/api\/?$/, '')
const fileUrl = (u) => (!u ? null : (/^https?:/.test(u) ? u : `${API}${u}`))
const vazio = { slug: '', nome: '', descricao: '', arquivo_url: '', obrigatorio: true, ordem: 1, ativo: true, nota: '' }

function Editor({ termo, onClose, onSaved }) {
  const [f, setF] = useState(termo ? { ...vazio, ...termo, nota: '' } : vazio)
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setF({ ...f, [k]: v })
  async function salvar() {
    if (!f.nome.trim()) return errorModal('Informe o nome do documento.')
    setBusy(true)
    try {
      if (termo?.id) await api.put(`/termos/${termo.id}`, f)
      else await api.post('/termos', f)
      await successModal('Documento salvo.', { title: 'Salvo' })
      onSaved()
    } catch (e) { errorModal(e?.response?.data?.detail || 'Não foi possível salvar') } finally { setBusy(false) }
  }
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-5 space-y-3 tc-pop" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-heading font-bold text-graphite">{termo?.id ? 'Editar documento' : 'Novo documento'}</p>
          <button onClick={onClose} className="text-gray-400 hover:text-graphite"><X size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Nome *</label><input className={inp} value={f.nome} onChange={(e) => set('nome', e.target.value)} /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Slug</label><input className={inp} value={f.slug} onChange={(e) => set('slug', e.target.value)} placeholder="termo_adesao" /></div>
          <div className="sm:col-span-2"><label className="block text-xs font-medium text-gray-600 mb-1">Descrição</label><textarea className={inp} rows={2} value={f.descricao} onChange={(e) => set('descricao', e.target.value)} /></div>
          <div className="sm:col-span-2"><label className="block text-xs font-medium text-gray-600 mb-1">URL do arquivo (PDF)</label><input className={inp} value={f.arquivo_url} onChange={(e) => set('arquivo_url', e.target.value)} placeholder="/media/termos/..." /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Ordem</label><input className={inp} type="number" value={f.ordem} onChange={(e) => set('ordem', Number(e.target.value))} /></div>
          <div className="flex items-end gap-4">
            <label className="flex items-center gap-1.5 text-sm text-gray-700"><input type="checkbox" checked={!!f.obrigatorio} onChange={(e) => set('obrigatorio', e.target.checked)} /> Obrigatório</label>
            <label className="flex items-center gap-1.5 text-sm text-gray-700"><input type="checkbox" checked={!!f.ativo} onChange={(e) => set('ativo', e.target.checked)} /> Ativo</label>
          </div>
          <div className="sm:col-span-2"><label className="block text-xs font-medium text-gray-600 mb-1">Nota da versão (opcional)</label><input className={inp} value={f.nota} onChange={(e) => set('nota', e.target.value)} placeholder="ex.: revisão de cláusulas" /></div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="text-sm text-gray-500 px-3 py-2">Cancelar</button>
          <button onClick={salvar} disabled={busy} className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2 text-sm font-bold shadow-md disabled:opacity-50">{busy ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}

export default function TermosAdmin() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(null)   // id do termo com histórico aberto
  const [editor, setEditor] = useState(false) // false | null(novo) | termo(editar)

  const load = useCallback(() => {
    setLoading(true)
    api.get('/termos').then((r) => setRows(r.data?.data || [])).catch(() => setRows([])).finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  if (loading) return <div className="flex justify-center py-20"><Loader text="Carregando documentos..." /></div>

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-extrabold text-graphite flex items-center gap-2">
            <FileText size={24} className="text-accent-500" /> Termos e Documentos
          </h1>
          <p className="text-sm text-gray-500 mt-1">Documentos versionados da plataforma — aceites, pendências e histórico.</p>
        </div>
        <button onClick={() => setEditor(null)} className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-4 py-2 text-sm font-bold shadow-md"><Plus size={16} /> Novo documento</button>
      </div>

      <div className="space-y-3">
        {rows.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-400"><FileText size={38} className="mx-auto mb-2 opacity-30" /><p>Nenhum documento cadastrado</p></div>
        ) : rows.map((t) => (
          <div key={t.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-graphite flex items-center gap-2">{t.nome}
                  <span className="text-[11px] font-bold text-accent-700 bg-accent-50 rounded-full px-2 py-0.5">v{t.versao}</span>
                  {t.obrigatorio && <span className="text-[10px] uppercase font-bold text-rose-600">obrigatório</span>}
                  {!t.ativo && <span className="text-[10px] uppercase font-bold text-gray-400">inativo</span>}
                </p>
                {t.descricao && <p className="text-xs text-gray-500 mt-0.5 max-w-2xl">{t.descricao}</p>}
                <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-gray-600">
                  <span className="inline-flex items-center gap-1"><CheckCircle2 size={13} className="text-emerald-600" /> {t.aceites_versao_atual ?? 0} aceites (v atual)</span>
                  <span className="inline-flex items-center gap-1"><Users size={13} className="text-gray-400" /> {t.licenciados_ativos ?? 0} licenciados</span>
                  <span className="inline-flex items-center gap-1"><Clock size={13} className="text-amber-500" /> {t.pendentes ?? 0} pendentes</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {fileUrl(t.arquivo_url) && <a href={fileUrl(t.arquivo_url)} target="_blank" rel="noreferrer" className="text-xs text-accent-700 border border-accent-200 rounded-lg px-2.5 py-1.5 hover:bg-accent-50 inline-flex items-center gap-1"><Download size={13} /> Abrir</a>}
                <button onClick={() => setEditor(t)} className="text-xs text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 inline-flex items-center gap-1"><Pencil size={13} /> Editar</button>
              </div>
            </div>
            {(t.historico || []).length > 0 && (
              <div className="border-t border-gray-50">
                <button onClick={() => setOpen(open === t.id ? null : t.id)} className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-gray-500 hover:bg-gray-50">
                  <span>Histórico de versões ({t.historico.length})</span>
                  <ChevronDown size={14} className={`transition-transform ${open === t.id ? 'rotate-180' : ''}`} />
                </button>
                {open === t.id && (
                  <table className="w-full text-sm">
                    <tbody>
                      {t.historico.map((h, i) => (
                        <tr key={i} className="border-t border-gray-50">
                          <td className="px-4 py-2 text-gray-500 w-16">v{h.versao}</td>
                          <td className="px-4 py-2 text-gray-700">{h.nota || '-'}</td>
                          <td className="px-4 py-2 text-gray-400 text-xs whitespace-nowrap">{h.usuario || 'Sistema'}</td>
                          <td className="px-4 py-2 text-gray-400 text-xs text-right whitespace-nowrap">{fmt(h.data)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {editor !== false && <Editor termo={editor} onClose={() => setEditor(false)} onSaved={() => { setEditor(false); load() }} />}
    </div>
  )
}
